import { useSyncExternalStore } from 'react';
import { collection, onSnapshot, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { readJSON, writeJSON, subscribeKey } from './deviceStore';
import { currentProfile, useProfile } from './profile';
import { inAudience } from '../utils/audience';

/*
 * Notifications: messages the department sends to the app. Documents live in `notifications`
 * (written only by Cloud Functions) and each carries an audience; this device shows the ones
 * its profile belongs to, for 30 days. Read and cleared state are the reader's own and sync
 * with the profile like bookmarks do.
 */

const READ_KEY = 'notifications-read';
const GONE_KEY = 'notifications-dismissed';
const MAX = 50;

// `?notifications=reset` on the URL forgets read and cleared state once (reviewing)
if (typeof window !== 'undefined' && /[?&]notifications=reset/.test(window.location.search)) { try { localStorage.removeItem(READ_KEY); localStorage.removeItem(GONE_KEY); } catch { /* ignore */ } }

const ids = (key) => { const v = readJSON(key, []); return Array.isArray(v) ? v : []; };

let docs = []; // every live notification document, newest first
let snapshot = { all: [], read: ids(READ_KEY), gone: ids(GONE_KEY) };
const listeners = new Set();
const emit = () => { snapshot = { all: docs, read: ids(READ_KEY), gone: ids(GONE_KEY) }; listeners.forEach((l) => l()); };
const subscribe = (cb) => { listeners.add(cb); return () => listeners.delete(cb); };

const toMillis = (t) => (t && t.toMillis ? t.toMillis() : typeof t === 'number' ? t : 0);

/** Call once at the app root: follows the collection and announces changes */
export const startNotifications = () => {
  subscribeKey(READ_KEY, emit);
  subscribeKey(GONE_KEY, emit);
  // Only delivered documents carry sentAt, so ordering by it also leaves scheduled ones out (no composite index needed)
  const q = query(collection(db, 'notifications'), orderBy('sentAt', 'desc'), limit(MAX));
  return onSnapshot(q, (snap) => {
    const now = Date.now();
    docs = [];
    snap.forEach((d) => {
      const n = d.data();
      const sentAt = toMillis(n.sentAt);
      const expiresAt = toMillis(n.expiresAt);
      if (n.status !== 'sent' || !sentAt || (expiresAt && expiresAt < now)) return;
      docs.push({ id: d.id, title: n.title || '', body: n.body || '', link: n.link || null, from: n.fromLabel || 'Department of Anesthesiology', audience: n.audience || {}, audienceLabel: n.audienceLabel || 'Everyone', sentAt });
    });
    emit();
  }, (e) => console.warn('[notifications] listen failed', e.message));
};

const visibleFor = (state, profile) => state.all
  .filter((n) => !state.gone.includes(n.id) && inAudience(n.audience, profile))
  .map((n) => ({ ...n, read: state.read.includes(n.id) }));

/** Notifications for this device and profile, newest first, with read state and actions */
export const useNotifications = () => {
  const state = useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
  const profile = useProfile();
  const items = visibleFor(state, profile);
  const unread = items.filter((n) => !n.read).length;
  const markRead = (id) => { if (state.read.includes(id)) return; writeJSON(READ_KEY, [...state.read, id]); emit(); };
  const markAllRead = () => { writeJSON(READ_KEY, [...new Set([...state.read, ...items.map((n) => n.id)])]); emit(); };
  const dismiss = (id) => { writeJSON(GONE_KEY, [...new Set([...state.gone, id])]); if (!state.read.includes(id)) writeJSON(READ_KEY, [...state.read, id]); emit(); };
  const dismissAll = () => { writeJSON(GONE_KEY, [...new Set([...state.gone, ...items.map((n) => n.id)])]); writeJSON(READ_KEY, [...new Set([...state.read, ...items.map((n) => n.id)])]); emit(); };
  return { items, unread, markRead, markAllRead, dismiss, dismissAll };
};

/** Unread count without React, for the app icon badge */
export const unreadCount = () => visibleFor(snapshot, currentProfile()).filter((n) => !n.read).length;

/** Keep the home-screen icon badge in step with the unread count (iOS 16.4+ installed, Android, desktop) */
export const startAppBadge = () => {
  const apply = () => {
    if (typeof navigator === 'undefined' || !navigator.setAppBadge) return;
    const n = unreadCount();
    (n > 0 ? navigator.setAppBadge(n) : navigator.clearAppBadge()).catch(() => {});
    // The service worker reads this when a push arrives while the app is closed (see service-worker.js)
    if (typeof caches !== 'undefined') caches.open('app-state').then((c) => c.put('/__unread', new Response(String(n)))).catch(() => {});
  };
  apply();
  return subscribe(apply);
};

export const whenLabel = (ts) => {
  const d = new Date(ts); const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (sameDay) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
